import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import {
  BiRefresh,
  Box,
  Button,
  ControllerDataGrid,
  GridColDef,
  GridRowScrollEndParams,
  GridRowsProp,
  GridSortModel,
  Typography,
} from '@bymason-private/legacy-pattern-components';
import { useLoadInfiniteProvisionSession } from '@bymason/controller-api-hooks';
import { formatLongDateTimeString } from '@bymason/utils';
import { useToast, DEFAULT_BOTTOM_SCROLL_THRESHOLD } from '../../../';

export default function ProvisionedDeviceList({ sessionId }: { sessionId: string }) {
  const { authToken = '' } = useAuth();
  const [setToastMessage, setToastSeverity] = useToast();
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'createdAt',
      sort: 'desc',
    },
  ]);
  const [options, setOptions] = useState({ sessionId: sessionId, limit: 20, ...sortModel[0] });
  const [session, sessionError, sessionBusy, , , , sessionClearData] = useLoadInfiniteProvisionSession(
    authToken,
    options,
    isScrolledToBottom
  );

  const handleRowsScrollEnd = (params: GridRowScrollEndParams) => {
    setIsScrolledToBottom(params.virtualRowsCount > 0);
  };

  const onSortModelChange = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
    setOptions((old) => {
      return { ...old, direction: sortModel[0]?.sort, orderBy: sortModel[0]?.field };
    });
  };

  const gridColumns: GridColDef[] = [
    {
      field: 'imei',
      headerName: 'IMEI',
      type: 'string',
      flex: 1,
      sortable: false,
    },
    {
      field: 'uuid',
      headerName: 'UUID',
      type: 'string',
      flex: 2,
      sortable: false,
    },
    {
      field: 'createdAt',
      headerName: 'Provisioned at',
      type: 'string',
      flex: 1,
      valueFormatter: (params) => {
        return params.value ? formatLongDateTimeString(params.value as Date) : '-';
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated at',
      type: 'string',
      flex: 1,
      valueFormatter: (params) => {
        return params.value ? formatLongDateTimeString(params.value as Date) : '-';
      },
    },
  ];

  const gridRows: GridRowsProp = useMemo(
    () =>
      session?.devices
        ? session.devices.map((device) => ({
            imei: device.imei,
            uuid: device.deviceID,
            createdAt: device.createdAt,
            updatedAt: device.updatedAt,
            id: device.deviceID,
          }))
        : [],
    [session?.devices]
  );

  useEffect(() => {
    if (sessionError && !sessionBusy) {
      setToastMessage(sessionError);
      setToastSeverity('error');
    }
  }, [sessionBusy, sessionError, setToastMessage, setToastSeverity]);

  useEffect(() => {
    // These effects should only run when the devices length changes
    setIsScrolledToBottom(!session?.devices?.length);
  }, [session?.devices]);

  return (
    <Box flex={1} overflow='auto'>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          my: 1,
        }}
      >
        <Typography fontWeight='bold'>Provisioned devices this session</Typography>
        <Button color='info' startIcon={<BiRefresh />} onClick={sessionClearData}>
          Refresh table
        </Button>
      </Box>
      <Box height='85%'>
        <ControllerDataGrid
          sortModel={sortModel}
          columns={gridColumns}
          disableSelectionOnClick
          onSortModelChange={onSortModelChange}
          components={{
            NoRowsOverlay: NoDevicesOverlay,
          }}
          loading={sessionBusy}
          rows={gridRows}
          autoHeight={false}
          density='standard'
          onRowsScrollEnd={handleRowsScrollEnd}
          disableColumnReorder={false}
          disableColumnResize={false}
          disableColumnSelector={false}
          disableDensitySelector={false}
          disableMultipleColumnsSorting
          scrollEndThreshold={DEFAULT_BOTTOM_SCROLL_THRESHOLD}
          sortingMode='server'
          filterMode='server'
        />
      </Box>
    </Box>
  );
}

const NoDevicesOverlay = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
    }}
  >
    <Typography fontWeight='bold' color='grayText'>
      No devices provisioned, yet
    </Typography>
  </Box>
);
